'use client';

import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import Link from 'next/link';
import './machine.css';

/* All styling lives in machine.css (scoped to .ml-page).
   Images go in public/assets/images/ .
   "Real business use cases" cards: har card ka image path aur link neeche `useCases` array me `img` / `href` field me badlo. */

type IconName =
  | 'db' | 'gear' | 'cloud' | 'monitor' | 'target' | 'users' | 'chart'
  | 'bulb' | 'shield' | 'arrow' | 'chevron';

function Icon({ name }: { name: IconName }) {
  const p: Record<IconName, ReactNode> = {
    db: <><ellipse cx="12" cy="6" rx="7" ry="3" /><path d="M5 6v6c0 1.7 3 3 7 3s7-1.3 7-3V6M5 12v6c0 1.7 3 3 7 3s7-1.3 7-3v-6" /></>,
    gear: <><circle cx="12" cy="12" r="3" /><path d="M12 3v3M12 18v3M3 12h3M18 12h3M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1M18.4 5.6l-2.1 2.1M7.7 16.3l-2.1 2.1" /></>,
    cloud: <><path d="M7 18a4 4 0 0 1-.5-8A5.5 5.5 0 0 1 17 8.5 4.5 4.5 0 0 1 17 18" /><path d="M12 12v8M9 15l3-3 3 3" /></>,
    monitor: <><rect x="3" y="4" width="18" height="12" rx="1.5" /><path d="M8 20h8M12 16v4M6 12l3-3 3 2 4-4" /></>,
    target: <><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="5" /><circle cx="12" cy="12" r="1.5" /></>,
    users: <><circle cx="9" cy="8" r="3" /><path d="M3 20c0-3.3 2.7-6 6-6s6 2.7 6 6M16 5a3 3 0 0 1 0 6M18 14c1.8.8 3 2.6 3 5" /></>,
    chart: <><path d="M4 20V4M4 20h16" /><rect x="8" y="12" width="3" height="6" /><rect x="13" y="8" width="3" height="10" /></>,
    bulb: <><path d="M9 18h6M10 21h4M12 3a6 6 0 0 0-3.5 10.9c.6.5 1 1.2 1 2.1h5c0-.9.4-1.6 1-2.1A6 6 0 0 0 12 3z" /></>,
    shield: <><path d="M12 3 5 6v5c0 4.5 3 8 7 10 4-2 7-5.5 7-10V6z" /><path d="m9 12 2 2 4-4" /></>,
    arrow: <><path d="M5 12h14M13 6l6 6-6 6" /></>,
    chevron: <><path d="m9 6 6 6-6 6" /></>,
  };
  return (
    <svg className="ml-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {p[name]}
    </svg>
  );
}

/* ============================== DATA ============================== */

const partners = ['SAP', 'Salesforce', 'Microsoft', 'AWS', 'Google Cloud'];

const capabilities: { icon: IconName; title: string; text: string }[] = [
  { icon: 'db', title: 'Model Development', text: 'Design and train tailored ML models using your data.' },
  { icon: 'gear', title: 'Model Optimisation', text: 'Improve accuracy, performance and scalability.' },
  { icon: 'cloud', title: 'Deployment & Integration', text: 'Deploy models into your business systems and workflows.' },
  { icon: 'monitor', title: 'Monitoring & Management', text: 'Continuously monitor model performance and retrain as needed.' },
];

const problems: { icon: IconName; text: string }[] = [
  { icon: 'target', text: 'Predict demand, sales and customer behaviour' },
  { icon: 'users', text: 'Detect risk, fraud and anomalies' },
  { icon: 'gear', text: 'Optimise operations and resource allocation' },
  { icon: 'chart', text: 'Personalise customer experiences' },
  { icon: 'bulb', text: 'Enable data-driven decision making' },
];

/* img = full path from public/ folder (extension ke saath). Har card ke liye alag file daalo.
   href = "Learn more" link. */
const useCases: { title: string; text: string; img: string; href: string }[] = [
  {
    title: 'Demand Forecasting',
    text: 'Predict product demand and optimise inventory.',
    img: '/assets/images/Demand.jpg',
    href: '/solutions/inventory-intelligence',
  },
  {
    title: 'Risk & Fraud Detection',
    text: 'Identify anomalies and reduce fraud.',
    img: '/assets/images/financial-performance.jpg',
    href: '/solutions/cash-working-capital',
  },
  {
    title: 'Predictive Maintenance',
    text: 'Anticipate equipment failures and minimise downtime.',
    img: '/assets/images/machine.jpg',
    href: '/solutions/asset-intelligence',
  },
  {
    title: 'Customer Analytics',
    text: 'Understand customer behaviour and enable personalised engagement.',
    img: '/assets/images/low-sales.jpg',
    href: '/solutions/customer',
  },
];

const approach: { icon: IconName; title: string; text: string }[] = [
  { icon: 'gear', title: 'Understand', text: 'Define business goals and data landscape.' },
  { icon: 'db', title: 'Build', text: 'Develop and validate ML models.' },
  { icon: 'cloud', title: 'Deploy', text: 'Integrate into business processes.' },
  { icon: 'chart', title: 'Measure', text: 'Track impact and continuously improve.' },
];

const stats = [
  { value: '30%', label: 'Improvement in forecast accuracy' },
  { value: '25%', label: 'Reduction in operational costs' },
  { value: '40%', label: 'Faster decision-making' },
  { value: '36%', label: 'Increase in customer engagement' },
];

const whyOrane: { icon: IconName; title: string; text: string }[] = [
  { icon: 'chart', title: 'Business-first approach', text: 'We focus on measurable business outcomes.' },
  { icon: 'users', title: 'Domain expertise', text: 'Experience across industries and enterprise data environments.' },
  { icon: 'gear', title: 'End-to-end delivery', text: 'From data preparation to deployment and monitoring.' },
  { icon: 'shield', title: 'Responsible AI', text: 'Built-in governance, fairness and compliance.' },
];

/* ===================== SCROLL REVEAL ===================== */
/* One fade-up on section content. Content stays visible if JS or IntersectionObserver is missing. */
function useReveal() {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const root = ref.current;
    if (!root || typeof IntersectionObserver === 'undefined') return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const io = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('rv--in');
            io.unobserve(e.target);
          }
        }),
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );
    root.querySelectorAll<HTMLElement>('.sec-head, .cap, .prob, .uc-card, .flow-step, .stat, .why').forEach((n, i) => {
      n.classList.add('rv');
      n.style.setProperty('--rv-delay', `${(i % 5) * 70}ms`);
      io.observe(n);
    });
    return () => io.disconnect();
  }, []);
  return ref;
}

/* ============================== PAGE ============================== */

export default function MachineLearning() {
  const ref = useReveal();

  return (
    <div className="ml-page" ref={ref}>
      <main>
        {/* HERO */}
        <section className="ml-hero">
          <div className="ml-hero-bg"></div>
          <div className="ml-hero-overlay"></div>
          <div className="op-container ml-hero-inner">
            <div className="ml-hero-content">
              <h1 className="ml-hero-title">
                Turn your data into a competitive advantage with <span>Machine Learning.</span>
              </h1>
              <p className="ml-hero-text">
                We design and deploy machine learning models that solve real business problems,
                automate decisions and create measurable value across your enterprise.
              </p>
              <div className="ml-actions">
                <Link className="btn-primary" href="/tellusyourproblem#tell-details">Talk to Our ML Experts</Link>
                <Link className="btn-outline" href="#use-cases">Explore Use Cases</Link>
              </div>
            </div>
          </div>
        </section>

        {/* TRUSTED BY */}
        <section className="ml-trust">
          <div className="op-container ml-trust-inner">
            <p className="ml-trust-label">Trusted to deliver AI solutions across industries.</p>
            <ul className="ml-trust-list">
              {partners.map((n) => (
                <li key={n}>{n}</li>
              ))}
            </ul>
          </div>
        </section>

        {/* WHAT WE DO */}
        <section className="op-section">
          <div className="op-container">
            <div className="what-grid">
              <div className="sec-head what-head">
                <h2 className="sec-title">
                  What we do in <span>Machine Learning</span>
                </h2>
                <p className="sec-sub">
                  We build, train and deploy machine learning models using your enterprise data to help
                  you predict, automate and optimise key business decisions.
                </p>
                <Link className="text-link" href="/whatwedo/Ai-and-Intelligence">
                  Explore all capabilities <Icon name="arrow" />
                </Link>
              </div>
              <div className="cap-grid">
                {capabilities.map((c) => (
                  <article className="cap" key={c.title}>
                    <Icon name={c.icon} />
                    <h3 className="card-title">{c.title}</h3>
                    <p className="card-text">{c.text}</p>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* SOLVE COMPLEX PROBLEMS */}
        <section className="op-section op-section--ruled">
          <div className="op-container">
            <div className="solve-grid">
              <figure className="solve-photo" role="img" aria-label="Analyst reviewing machine learning dashboards on multiple screens">
                <figcaption>From data to real business results.</figcaption>
              </figure>
              <div>
                <div className="sec-head solve-head">
                  <h2 className="sec-title">Solve your most complex business problems.</h2>
                </div>
                <ul className="prob-list">
                  {problems.map((p) => (
                    <li className="prob" key={p.text}>
                      <Icon name={p.icon} />
                      <span>{p.text}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* USE CASES */}
        <section className="op-section op-section--ruled" id="use-cases">
          <div className="op-container">
            <div className="uc-head">
              <div className="sec-head">
                <h2 className="sec-title">Real business use cases</h2>
              </div>
              <Link className="text-link" href="/52problem">
                Explore more use cases <Icon name="arrow" />
              </Link>
            </div>
            <div className="uc-grid">
              {useCases.map((u) => (
                <article className="card uc-card" key={u.title}>
                  <div className="uc-img" style={{ background: '#f4f8fd' }}>
                    <img src={u.img} alt={u.title} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center', display: 'block', maxWidth: 'none' }} />
                  </div>
                  <div className="uc-body">
                    <h3 className="card-title">{u.title}</h3>
                    <p className="card-text">{u.text}</p>
                    <Link className="text-link" href={u.href}>
                      Learn more <Icon name="arrow" />
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* APPROACH: chevron flow */}
        <section className="op-section op-section--ruled">
          <div className="op-container">
            <div className="sec-head">
              <h2 className="sec-title">Our approach</h2>
            </div>
            <ol className="flow">
              {approach.map((a, i) => (
                <li className="flow-step" key={a.title}>
                  <span className="flow-num">{String(i + 1).padStart(2, '0')}</span>
                  <div className="flow-body">
                    <h3 className="card-title">{a.title}</h3>
                    <p className="card-text">{a.text}</p>
                  </div>
                  <Icon name={a.icon} />
                  {i < approach.length - 1 && <span className="flow-arrow"><Icon name="chevron" /></span>}
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* IMPACT: big numbers */}
        <section className="op-section op-section--ruled">
          <div className="op-container">
            <div className="impact-grid">
              <div className="sec-head impact-head">
                <h2 className="sec-title">Smarter models. Stronger outcomes.</h2>
                <Link className="btn-outline" href="/solutions">Explore ML Solutions</Link>
              </div>
              <div className="stat-grid">
                {stats.map((s) => (
                  <div className="stat" key={s.label}>
                    <strong>{s.value}</strong>
                    <span>{s.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* WHY ORANE */}
        <section className="op-section op-section--ruled">
          <div className="op-container">
            <div className="sec-head">
              <h2 className="sec-title">Why Orane for Machine Learning?</h2>
            </div>
            <div className="why-grid">
              {whyOrane.map((w) => (
                <div className="why" key={w.title}>
                  <Icon name={w.icon} />
                  <div>
                    <h3 className="card-title">{w.title}</h3>
                    <p className="card-text">{w.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="ml-cta" id="contact">
          <div className="ml-cta-bg"></div>
          <div className="ml-cta-overlay"></div>
          <div className="op-container ml-cta-inner">
            <h2 className="ml-cta-title">Talk to our machine learning experts.</h2>
            <p className="ml-cta-text">Tell us about your data and your business problem. We will show you where ML can help.</p>
            <Link className="btn-primary" href="/tellusyourproblem#tell-details">Tell Us Your Problem</Link>
          </div>
        </section>
      </main>
    </div>
  );
}